import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTiptap } from "@tiptap/react";
import {
  COLOR_PICKER_POPUP_MARGIN,
  COLOR_PICKER_POPUP_TOTAL_HEIGHT,
  ColorPickerPopup,
  IColorPickerPopupPosition,
} from "components/colorpicker/popup/ColorPickerPopup";
import { WEBSITE_BUILDER_PORTAL } from "../../WebsiteBuilderEditor";
import { useWebsiteBuilderEditorContext } from "../../websiteBuilderEditorContext";

interface IWebsiteBuilderRichTextColorPickerProps {
  elementId: string;
  colorButtonRef: React.MutableRefObject<HTMLButtonElement>;
  closeColorPicker: () => void;
}

interface IWebsiteBuilderRichTextColorPickerState {
  position: IColorPickerPopupPosition;
  color: string;
}

export function WebsiteBuilderRichTextColorPicker(props: IWebsiteBuilderRichTextColorPickerProps) {
  const { elementId, colorButtonRef, closeColorPicker } = props;

  const { editor } = useTiptap();
  const { activePageDomNodeRefMap } = useWebsiteBuilderEditorContext();

  const colorPickerRef = useRef<HTMLDivElement>(null);
  const debouncedColorTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const [state, setState] = useState<IWebsiteBuilderRichTextColorPickerState>(null);

  useLayoutEffect(() => {
    const element = activePageDomNodeRefMap.current.get(elementId);
    const boundingRect = element?.getBoundingClientRect();

    if (boundingRect == null) {
      closeColorPicker();
      return;
    }

    const x = boundingRect.left + (boundingRect.width / 2);
    let y = window.innerHeight - COLOR_PICKER_POPUP_TOTAL_HEIGHT;

    const topY = boundingRect.top - COLOR_PICKER_POPUP_TOTAL_HEIGHT;
    const bottomY = boundingRect.bottom + COLOR_PICKER_POPUP_TOTAL_HEIGHT;

    if (bottomY <= window.innerHeight) {
      y = boundingRect.bottom + COLOR_PICKER_POPUP_MARGIN;
    } else if (topY >= 0) {
      y = topY;
    }

    const color: unknown = editor.getAttributes("textStyle").color;
    setState({ position: { x, y }, color: typeof color === "string" ? color : null });

    return () => clearTimeout(debouncedColorTimerRef.current);
  }, []);

  useEffect(() => {
    function handlePointerDown(event: PointerEvent) {
      if (
        event.target instanceof Node &&
        !colorButtonRef.current?.contains(event.target) &&
        !colorPickerRef.current?.contains(event.target)
      ) {
        closeColorPicker();
      }
    }

    document.addEventListener("pointerdown", handlePointerDown, { capture: true });

    return () => document.removeEventListener("pointerdown", handlePointerDown, { capture: true });
  }, []);

  function updateColor(color: string) {
    clearTimeout(debouncedColorTimerRef.current);

    debouncedColorTimerRef.current = setTimeout(() => {
      if (color == null) {
        editor.commands.unsetColor();
      } else {
        editor.commands.setColor(color);
      }
    }, 500);

    setState(prev => ({ ...prev, color }));
  }

  if (state != null) {
    return (
      <ColorPickerPopup
        ref={colorPickerRef}
        position={state.position}
        hexColor={state.color}
        updateHexColor={updateColor}
        closePopup={closeColorPicker}
        portalParentId={WEBSITE_BUILDER_PORTAL}
      />
    );
  } else {
    return null;
  }
}
